import { produce } from 'immer';

import { SavedSearch } from '@/data/SavedSearchApi/SavedSearchApi';

import { storeFactory } from './storeFactory';

interface SavedSearchStoreStateData {
  savedSearches: SavedSearch[] | null;
  mappedSaveSearch: Map<string, SavedSearch>;
  savedSearchesLoaded: boolean;
  savedSearchesInitialised: boolean;
}

interface SavedSearchStoreStateFunctions {
  setSavedSearchesLoading: () => void;
  setSavedSearchesInitialised: (initialised: SavedSearchStoreStateData['savedSearchesInitialised']) => void;
  setSavedSearchesData: (savedSearches: SavedSearchStoreStateData['savedSearches']) => void;
  /** Empty the store. To be used when user logs out for example */
  clearSavedSearchesData: () => void;
}

const {
  store: savedSearchStore,
  StoreProvider: SavedSearchStoreProvider,
  useCreateStore: useCreateSavedSearchStore,
} = storeFactory<SavedSearchStoreStateData, SavedSearchStoreStateFunctions>(
  () => ({
    savedSearches: null,
    mappedSaveSearch: new Map(),
    savedSearchesLoaded: false,
    savedSearchesInitialised: false,
  }),
  (set) => ({
    setSavedSearchesLoading: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.savedSearchesLoaded = false;
        }),
      );
    },
    setSavedSearchesInitialised: (savedSearchesInitialised: SavedSearchStoreStateData['savedSearchesInitialised']) => {
      set((state) =>
        produce(state, (draft) => {
          draft.savedSearchesInitialised = savedSearchesInitialised;
        }),
      );
    },
    setSavedSearchesData: (savedSearches: SavedSearchStoreStateData['savedSearches']) => {
      set((state) =>
        produce(state, (draft) => {
          draft.savedSearches = savedSearches;
          draft.savedSearchesLoaded = true;
          draft.savedSearchesInitialised = true;
          draft.mappedSaveSearch = mapSavedSearches(savedSearches);
        }),
      );
    },
    clearSavedSearchesData: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.savedSearches = null;
          draft.mappedSaveSearch = new Map();
          draft.savedSearchesLoaded = false;
          draft.savedSearchesInitialised = false;
        }),
      );
    },
  }),
);

export { savedSearchStore, SavedSearchStoreProvider, useCreateSavedSearchStore };

const mapSavedSearches = (
  savedSearches: SavedSearch[] | null,
): SavedSearchStoreStateData['mappedSaveSearch'] => {
  if (!savedSearches) {
    return new Map();
  }

  return new Map(savedSearches.filter((item) => item.plp_slug).map((item) => [item.plp_slug, item]));
};
